/**
 * ListModals — Panorama List + Asset (POI) List modals for SitesPage. apiService imported directly (same module singleton).
 *
 * JSX moved verbatim from src/components/SitesPage.tsx (SitesPage split 3/3).
 * Rendered DOM is byte-equivalent (classNames, text, conditional structure, keys unchanged).
 * All page state/handlers arrive as props; the interface + SitesPage invocation are
 * generated from a single prop list so they cannot drift.
 */
import React from 'react';
import { apiService } from '../../services/ApiService';
import { Asset, AssetFile } from '../../services/AssetService';

interface ListModalsProps {
  canManageAsset: (asset: Asset | null) => boolean;
  deletingAllPOIs: boolean;
  deletingPOI: string | null;
  editedPOIs: Record<string, any>;
  isEditMode: boolean;
  loadingPOIs: boolean;
  loadingPanoramas: boolean;
  panoramas: any[];
  pois: any[];
  savingPOIs: boolean;
  selectedAsset: Asset | null;
  selectedAssetE57Sources: AssetFile[];
  selectedPOIs: Set<string>;
  showPOIListModal: boolean;
  showPanoramaListModal: boolean;
  setSelectedPOIs: React.Dispatch<React.SetStateAction<Set<string>>>;
  setShowPOIListModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPanoramaListModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleCancelEditMode: () => void;
  // Delete-ALL is admin-only on every surface (UnifiedPOITable already gated it; this
  // modal previously showed it to any write-permission user — converged 2026-07-06)
  isAdmin: boolean;
  handleDeleteAllPOIs: () => Promise<void>;
  handleDeletePOI: (poiId: string) => Promise<void>;
  handleEnterEditMode: () => void;
  handleOpenSetLocationsModal: () => Promise<void>;
  handleOpenSetSystemsModal: () => Promise<void>;
  handlePOIFieldChange: (poiId: string, field: string, value: any) => void;
  handleSaveAllEdits: () => Promise<void>;
}

const ListModals: React.FC<ListModalsProps> = ({
  canManageAsset,
  deletingAllPOIs,
  deletingPOI,
  editedPOIs,
  isEditMode,
  loadingPOIs,
  loadingPanoramas,
  panoramas,
  pois,
  savingPOIs,
  selectedAsset,
  selectedAssetE57Sources,
  selectedPOIs,
  showPOIListModal,
  showPanoramaListModal,
  setSelectedPOIs,
  setShowPOIListModal,
  setShowPanoramaListModal,
  handleCancelEditMode,
  isAdmin,
  handleDeleteAllPOIs,
  handleDeletePOI,
  handleEnterEditMode,
  handleOpenSetLocationsModal,
  handleOpenSetSystemsModal,
  handlePOIFieldChange,
  handleSaveAllEdits,
}) => {
  return (
    <>
      {/* Panorama List Modal */}
      {showPanoramaListModal && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Panoramas - {selectedAsset?.name}</h2>
                <button
                  onClick={() => setShowPanoramaListModal(false)}
                  className="btn btn-icon"
                  style={{ marginLeft: 'auto' }}
                >
                  ✕
                </button>
              </div>

              {loadingPanoramas ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading panoramas...</p>
                </div>
              ) : (
                <div className="panorama-list">
                  {panoramas.length === 0 ? (
                    <div className="no-panoramas">
                      <p>No panoramas found for this asset.</p>
                      <p>Upload an E57 file to extract panoramic images.</p>
                    </div>
                  ) : (
                    <>
                      <div className="panorama-summary">
                        <p><strong>{panoramas.length}</strong> panoramas extracted from {selectedAssetE57Sources.length || 1} E57 source{(selectedAssetE57Sources.length || 1) === 1 ? '' : 's'}</p>
                      </div>

                      <div className="panorama-grid">
                        {panoramas.map((panorama, index) => (
                          <div key={panorama.id} className="panorama-item">
                            <div className="panorama-preview">
                              {/* Audit bug 76: the viewer-size copy, not the full original, and only
                                  as it scrolls into view (the list used to fetch every photo at full
                                  size at once: 8,224 on the winery). A photo whose copy is still
                                  being made shows "Preparing…". */}
                              {panorama.viewerReady === false ? (
                                <div className="panorama-thumbnail panorama-thumbnail--preparing" role="status">Preparing…</div>
                              ) : (
                              <img
                                src={`/api/assets/${selectedAsset?.id}/panoramas/${panorama.id}/image?variant=viewer&token=${apiService.getToken() || ''}`}
                                alt={`Panorama ${index + 1}`}
                                className="panorama-thumbnail"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => {
                                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                                }}
                              />
                              )}
                            </div>
                            <div className="panorama-details">
                              <h4>Panorama {panorama.panoramaIndex + 1}</h4>
                              <p><strong>Position:</strong> ({panorama.position.x.toFixed(1)}, {panorama.position.y.toFixed(1)}, {panorama.position.z.toFixed(1)})</p>
                              <p><strong>Scan:</strong> {panorama.scanIndex + 1}</p>
                              {panorama.metadata?.setup_name && (
                                <p><strong>Setup:</strong> {panorama.metadata.setup_name}</p>
                              )}
                              {panorama.sourceFileName && (
                                <p><strong>Source:</strong> {panorama.sourceFileName}</p>
                              )}
                              {panorama.metadata?.fov && (
                                <p><strong>FOV:</strong> {panorama.metadata.fov}°</p>
                              )}
                              <div className="panorama-actions">
                                <a
                                  href={`/api/assets/${selectedAsset?.id}/panoramas/${panorama.id}/image?token=${apiService.getToken() || ''}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-small btn-secondary"
                                >
                                  View Full Size
                                </a>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button
                  onClick={() => setShowPanoramaListModal(false)}
                  className="btn btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Asset List Modal */}
      {showPOIListModal && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Assets - {selectedAsset?.name}</h2>
                <button
                  onClick={() => setShowPOIListModal(false)}
                  className="btn btn-icon"
                  style={{ marginLeft: 'auto' }}
                >
                  ✕
                </button>
              </div>

              {loadingPOIs ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading assets...</p>
                </div>
              ) : (
                <div className="poi-list">
                  {pois.length === 0 ? (
                    <div className="no-pois">
                      <p>No assets found for this site.</p>
                      <p>Upload a CSV file to add assets.</p>
                    </div>
                  ) : (
                    <>
                      <div className="poi-summary">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <p><strong>{pois.length}</strong> assets loaded{selectedPOIs.size > 0 && ` (${selectedPOIs.size} selected)`}</p>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {/* Edit controls - only for users with write access */}
                            {canManageAsset(selectedAsset) && (
                              <>
                                {/* Edit Mode Toggle Button */}
                                {!isEditMode ? (
                                  <button
                                    onClick={handleEnterEditMode}
                                    className="btn btn-primary"
                                    title="Enter edit mode to modify all fields"
                                  >
                                    Edit Table
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      onClick={handleSaveAllEdits}
                                      className="btn btn-primary"
                                      disabled={savingPOIs}
                                      title="Save all changes"
                                    >
                                      {savingPOIs ? 'Saving...' : 'Save All'}
                                    </button>
                                    <button
                                      onClick={handleCancelEditMode}
                                      className="btn btn-secondary"
                                      disabled={savingPOIs}
                                      title="Cancel editing"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                )}
                                {/* Set Systems button */}
                                <button
                                  onClick={handleOpenSetSystemsModal}
                                  className="btn btn-secondary"
                                  disabled={selectedPOIs.size === 0 || isEditMode}
                                  title={selectedPOIs.size > 0 ? `Set systems for ${selectedPOIs.size} selected assets` : 'Select assets using checkboxes first'}
                                >
                                  Set Systems{selectedPOIs.size > 0 ? ` (${selectedPOIs.size})` : ''}
                                </button>
                                {/* Set Locations button */}
                                <button
                                  onClick={handleOpenSetLocationsModal}
                                  className="btn btn-secondary"
                                  disabled={selectedPOIs.size === 0 || isEditMode}
                                  title={selectedPOIs.size > 0 ? `Set locations for ${selectedPOIs.size} selected assets` : 'Select assets using checkboxes first'}
                                >
                                  Set Locations{selectedPOIs.size > 0 ? ` (${selectedPOIs.size})` : ''}
                                </button>
                                {/* Delete All button — admin only (same gate as UnifiedPOITable) */}
                                {isAdmin && (
                                  <button
                                    onClick={handleDeleteAllPOIs}
                                    className="btn btn-danger"
                                    disabled={deletingAllPOIs || isEditMode}
                                    title="Delete all assets for this site"
                                  >
                                    {deletingAllPOIs ? 'Deleting All...' : 'Delete All Assets'}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="poi-table-container">
                        <table className="poi-table">
                          <thead>
                            <tr>
                              <th style={{ width: '40px' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedPOIs.size === pois.length && pois.length > 0}
                                  onChange={() => {
                                    if (selectedPOIs.size === pois.length) {
                                      setSelectedPOIs(new Set());
                                    } else {
                                      setSelectedPOIs(new Set(pois.map(p => p.id)));
                                    }
                                  }}
                                  style={{ cursor: 'pointer' }}
                                />
                              </th>
                              <th>Title</th>
                              <th>Subcategory</th>
                              <th>Position (X, Y, Z)</th>
                              <th>Model Coordinates</th>
                              <th>Normalized 2D Coordinates</th>
                              <th>Type</th>
                              <th>Group</th>
                              <th>Category</th>
                              <th>Importance</th>
                              <th>Visible</th>
                              <th>Asset ID</th>
                              <th>IFC GUID</th>
                              <th>Icon</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pois.map((poi, index) => (
                              <tr key={poi.id} style={{ backgroundColor: selectedPOIs.has(poi.id) ? 'rgba(72, 202, 228, 0.15)' : 'transparent' }}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedPOIs.has(poi.id)}
                                    onChange={() => {
                                      setSelectedPOIs(prev => {
                                        const newSet = new Set(prev);
                                        if (newSet.has(poi.id)) {
                                          newSet.delete(poi.id);
                                        } else {
                                          newSet.add(poi.id);
                                        }
                                        return newSet;
                                      });
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  />
                                </td>
                                {/* Title - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <input
                                      type="text"
                                      value={editedPOIs[poi.id]?.title || ''}
                                      onChange={(e) => handlePOIFieldChange(poi.id, 'title', e.target.value)}
                                      style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
                                    />
                                  ) : (
                                    <strong>{poi.title || `Asset ${index + 1}`}</strong>
                                  )}
                                </td>
                                {/* Subcategory - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <input
                                      type="text"
                                      value={editedPOIs[poi.id]?.subcategory || ''}
                                      onChange={(e) => handlePOIFieldChange(poi.id, 'subcategory', e.target.value)}
                                      style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
                                    />
                                  ) : (
                                    (() => {
                                      if (poi.subcategory) return poi.subcategory;
                                      try {
                                        const customData = typeof poi.custom_data === 'string' ? JSON.parse(poi.custom_data) : poi.custom_data;
                                        const objectType = customData?.ifc?.ifcProperties?.ObjectType;
                                        if (objectType && typeof objectType === 'string') {
                                          const match = objectType.match(/^(.*?)\s+\d+:/);
                                          return match ? match[1].trim() : objectType.trim();
                                        }
                                      } catch (error) {}
                                      return poi.poi_type_name || 'N/A';
                                    })()
                                  )}
                                </td>
                                {/* Position - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <div style={{ display: 'flex', gap: '2px' }}>
                                      <input type="number" step="0.1" value={editedPOIs[poi.id]?.x || ''} onChange={(e) => handlePOIFieldChange(poi.id, 'x', e.target.value)} placeholder="X" style={{ width: '50px', padding: '2px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px' }} />
                                      <input type="number" step="0.1" value={editedPOIs[poi.id]?.y || ''} onChange={(e) => handlePOIFieldChange(poi.id, 'y', e.target.value)} placeholder="Y" style={{ width: '50px', padding: '2px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px' }} />
                                      <input type="number" step="0.1" value={editedPOIs[poi.id]?.z || ''} onChange={(e) => handlePOIFieldChange(poi.id, 'z', e.target.value)} placeholder="Z" style={{ width: '50px', padding: '2px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px' }} />
                                    </div>
                                  ) : (
                                    poi.x && poi.y && poi.z ?
                                      `(${Number(poi.x).toFixed(1)}, ${Number(poi.y).toFixed(1)}, ${Number(poi.z).toFixed(1)})` :
                                      'No coordinates'
                                  )}
                                </td>
                                {/* Model Coordinates - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <div style={{ display: 'flex', gap: '2px' }}>
                                      <input type="number" step="0.1" value={editedPOIs[poi.id]?.model_coordinates_x || ''} onChange={(e) => handlePOIFieldChange(poi.id, 'model_coordinates_x', e.target.value)} placeholder="X" style={{ width: '50px', padding: '2px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px' }} />
                                      <input type="number" step="0.1" value={editedPOIs[poi.id]?.model_coordinates_y || ''} onChange={(e) => handlePOIFieldChange(poi.id, 'model_coordinates_y', e.target.value)} placeholder="Y" style={{ width: '50px', padding: '2px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px' }} />
                                      <input type="number" step="0.1" value={editedPOIs[poi.id]?.model_coordinates_z || ''} onChange={(e) => handlePOIFieldChange(poi.id, 'model_coordinates_z', e.target.value)} placeholder="Z" style={{ width: '50px', padding: '2px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px' }} />
                                    </div>
                                  ) : (
                                    <small style={{ fontFamily: 'monospace', fontSize: '11px', maxWidth: '150px', display: 'block', wordBreak: 'break-all' }}>
                                      {(() => {
                                        try {
                                          if (!poi.model_coordinates) return 'Not set';
                                          const coords = typeof poi.model_coordinates === 'string' ? JSON.parse(poi.model_coordinates) : poi.model_coordinates;
                                          return `x: ${Number(coords.x).toFixed(1)}, y: ${Number(coords.y).toFixed(1)}, z: ${Number(coords.z).toFixed(1)}`;
                                        } catch (e) {
                                          return poi.model_coordinates || 'Not set';
                                        }
                                      })()}
                                    </small>
                                  )}
                                </td>
                                {/* Normalized 2D Coordinates - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <div style={{ display: 'flex', gap: '2px' }}>
                                      <input type="number" step="0.1" value={editedPOIs[poi.id]?.normalized_2d_x || ''} onChange={(e) => handlePOIFieldChange(poi.id, 'normalized_2d_x', e.target.value)} placeholder="X" style={{ width: '50px', padding: '2px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px' }} />
                                      <input type="number" step="0.1" value={editedPOIs[poi.id]?.normalized_2d_y || ''} onChange={(e) => handlePOIFieldChange(poi.id, 'normalized_2d_y', e.target.value)} placeholder="Y" style={{ width: '50px', padding: '2px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px' }} />
                                      <input type="number" step="0.1" value={editedPOIs[poi.id]?.normalized_2d_z || ''} onChange={(e) => handlePOIFieldChange(poi.id, 'normalized_2d_z', e.target.value)} placeholder="Z" style={{ width: '50px', padding: '2px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px' }} />
                                    </div>
                                  ) : (
                                    <small style={{ fontFamily: 'monospace', fontSize: '11px', maxWidth: '150px', display: 'block', wordBreak: 'break-all' }}>
                                      {(() => {
                                        try {
                                          if (!poi.normalized_2d_coordinates) return 'Not set';
                                          const coords = typeof poi.normalized_2d_coordinates === 'string' ? JSON.parse(poi.normalized_2d_coordinates) : poi.normalized_2d_coordinates;
                                          return `x: ${Number(coords.x).toFixed(1)}, y: ${Number(coords.y).toFixed(1)}, z: ${Number(coords.z).toFixed(1)}`;
                                        } catch (e) {
                                          return poi.normalized_2d_coordinates || 'Not set';
                                        }
                                      })()}
                                    </small>
                                  )}
                                </td>
                                {/* Type - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <input
                                      type="text"
                                      value={editedPOIs[poi.id]?.poi_type_name || ''}
                                      onChange={(e) => handlePOIFieldChange(poi.id, 'poi_type_name', e.target.value)}
                                      style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
                                    />
                                  ) : (
                                    poi.poi_type_name || 'Unknown'
                                  )}
                                </td>
                                {/* Group - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <input
                                      type="text"
                                      value={editedPOIs[poi.id]?.poi_group_name || ''}
                                      onChange={(e) => handlePOIFieldChange(poi.id, 'poi_group_name', e.target.value)}
                                      style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
                                    />
                                  ) : (
                                    poi.poi_group_name || 'N/A'
                                  )}
                                </td>
                                {/* Category - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <input
                                      type="text"
                                      value={editedPOIs[poi.id]?.ifc_category || ''}
                                      onChange={(e) => handlePOIFieldChange(poi.id, 'ifc_category', e.target.value)}
                                      style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
                                    />
                                  ) : (
                                    poi.ifc_category || 'N/A'
                                  )}
                                </td>
                                {/* Importance - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <input
                                      type="number"
                                      min="0"
                                      max="10"
                                      step="0.1"
                                      value={editedPOIs[poi.id]?.importance || 1}
                                      onChange={(e) => handlePOIFieldChange(poi.id, 'importance', parseFloat(e.target.value))}
                                      style={{ width: '60px', padding: '4px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
                                    />
                                  ) : (
                                    poi.importance || 'N/A'
                                  )}
                                </td>
                                {/* Visible - Editable */}
                                <td style={{ textAlign: 'center' }}>
                                  {isEditMode ? (
                                    <input
                                      type="checkbox"
                                      checked={editedPOIs[poi.id]?.visibility_check !== false}
                                      onChange={(e) => handlePOIFieldChange(poi.id, 'visibility_check', e.target.checked)}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  ) : (
                                    poi.visibility_check ? '✅' : '❌'
                                  )}
                                </td>
                                {/* Asset ID - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <input
                                      type="text"
                                      value={editedPOIs[poi.id]?.poi_id || ''}
                                      onChange={(e) => handlePOIFieldChange(poi.id, 'poi_id', e.target.value)}
                                      style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px', fontFamily: 'monospace' }}
                                    />
                                  ) : (
                                    <small style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                                      {poi.poi_id || 'N/A'}
                                    </small>
                                  )}
                                </td>
                                {/* IFC GUID - Editable */}
                                <td>
                                  {isEditMode ? (
                                    <input
                                      type="text"
                                      value={editedPOIs[poi.id]?.ifc_element_guid || ''}
                                      onChange={(e) => handlePOIFieldChange(poi.id, 'ifc_element_guid', e.target.value)}
                                      style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '11px', fontFamily: 'monospace' }}
                                    />
                                  ) : (
                                    <small style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                                      {poi.ifc_element_guid || 'N/A'}
                                    </small>
                                  )}
                                </td>
                                {/* Icon - Editable */}
                                <td style={{ fontSize: '18px', textAlign: 'center' }}>
                                  {isEditMode ? (
                                    <input
                                      type="text"
                                      value={editedPOIs[poi.id]?.poi_type_icon || '📍'}
                                      onChange={(e) => handlePOIFieldChange(poi.id, 'poi_type_icon', e.target.value)}
                                      style={{ width: '40px', padding: '4px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '16px', textAlign: 'center' }}
                                    />
                                  ) : (
                                    poi.poi_type_icon || poi.icon || '📍'
                                  )}
                                </td>
                                {/* Actions */}
                                <td>
                                  {selectedAsset?.permission_level === 'write' && !isEditMode && (
                                    <button
                                      onClick={() => handleDeletePOI(poi.id)}
                                      className="btn btn-danger btn-small"
                                      disabled={deletingPOI === poi.id}
                                      title="Delete this asset"
                                    >
                                      {deletingPOI === poi.id ? '⏳' : '🗑️'}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button
                  onClick={() => setShowPOIListModal(false)}
                  className="btn btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListModals;
